import {
    Image, MergeRequest, RecipeListItem, Transformation, TransformationUpdate, TransformationWithUsage,
} from './types';
import { authService } from '../auth/AuthService.js';

export async function getTransformation(transformationId: string): Promise<Transformation> {
    const response = await fetch(`/api/transformation/${transformationId}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function createTransformation(transformation: Omit<Transformation, 'id'>): Promise<Transformation> {
    const response = await fetch('/api/transformation/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders(),
        },
        body: JSON.stringify(transformation),
    });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function updateTransformation(transformationId: string, transformationUpdate: TransformationUpdate): Promise<Transformation> {
    const response = await fetch(`/api/transformation/${transformationId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders(),
        },
        body: JSON.stringify(transformationUpdate),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to update transformation' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }

    return response.json();
}

export async function addTransformationImage(transformationId: string, file: Blob): Promise<Image> {
    const formData = new FormData();
    formData.append('file', file, 'image.jpg');
    const response = await fetch(`/api/transformation/${transformationId}/images`, {
        method: 'POST',
        headers: { ...authService.getAuthHeaders() },
        body: formData,
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to upload image' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function deleteTransformationImage(transformationId: string, imageId: string): Promise<void> {
    const response = await fetch(`/api/transformation/${transformationId}/images/${imageId}`, {
        method: 'DELETE',
        headers: { ...authService.getAuthHeaders() },
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to delete image' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
}

export async function setDefaultTransformationImage(transformationId: string, imageId: string): Promise<Transformation> {
    const response = await fetch(`/api/transformation/${transformationId}/images/${imageId}/default`, {
        method: 'PUT',
        headers: { ...authService.getAuthHeaders() },
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to set default image' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function searchTransformations(query: string, limit: number = 20): Promise<Transformation[]> {
    const params = new URLSearchParams({
        q: query,
        limit: limit.toString()
    });
    const response = await fetch(`/api/transformations/search?${params}`);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

/** Every transformation plus its recipe-usage count — for the explorer's table. */
export async function listTransformations(): Promise<TransformationWithUsage[]> {
    const response = await fetch('/api/transformations');
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function deleteTransformation(transformationId: string): Promise<void> {
    const response = await fetch(`/api/transformation/${transformationId}`, {
        method: 'DELETE',
        headers: { ...authService.getAuthHeaders() },
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to delete transformation' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
}

/** Merge one or more transformations into mergeRequest.targetId. */
export async function mergeTransformations(mergeRequest: MergeRequest): Promise<Transformation> {
    const response = await fetch('/api/transformations/merge', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders(),
        },
        body: JSON.stringify(mergeRequest),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to merge transformations' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}

/** Recipes that contain at least one node with this transformation. */
export async function getRecipesUsingTransformation(transformationId: string): Promise<RecipeListItem[]> {
    const response = await fetch(`/api/transformation/${transformationId}/recipes`);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}
